import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

const persistKey = 'ung_auth';

const loadPersisted = () => {
  try {
    const s = localStorage.getItem(persistKey);
    if (s) return JSON.parse(s);
  } catch {}
  return { user: null, accessToken: null, refreshToken: null, mustChangePassword: false };
};

const persist = (state) => {
  try {
    localStorage.setItem(
      persistKey,
      JSON.stringify({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        mustChangePassword: state.mustChangePassword,
      })
    );
  } catch {}
};

const initialState = loadPersisted();

export const login = createAsyncThunk('auth/login', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/login', payload);
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Erreur de connexion.');
  }
});

export const changePassword = createAsyncThunk('auth/changePassword', async (payload, { rejectWithValue }) => {
  try {
    await api.post('/auth/change-password', payload);
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Erreur lors du changement de mot de passe.');
  }
});

export const forgotPassword = createAsyncThunk('auth/forgotPassword', async (email, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/forgot-password', { email });
    return data.message;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Erreur.');
  }
});

export const resetPasswordWithOtp = createAsyncThunk(
  'auth/resetPasswordWithOtp',
  async (payload, { rejectWithValue }) => {
    try {
      await api.post('/auth/reset-password-with-otp', payload);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Code invalide ou expiré.');
    }
  }
);

export const fetchMe = createAsyncThunk('auth/me', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/auth/me');
    return data.user;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message);
  }
});

export const updateProfile = createAsyncThunk('auth/updateProfile', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await api.patch('/users/me', payload);
    return data.user;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Erreur lors de la mise à jour du profil.');
  }
});

export const uploadProfilePicture = createAsyncThunk('auth/uploadProfilePicture', async (file, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append('avatar', file);
    const { data } = await api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.user;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Erreur lors du téléchargement de la photo.');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    ...initialState,
    loading: false,
    error: null,
  },
  reducers: {
    setCredentials: (state, action) => {
      if (action.payload.accessToken) state.accessToken = action.payload.accessToken;
      if (action.payload.refreshToken !== undefined) state.refreshToken = action.payload.refreshToken;
      persist(state);
    },
    setUser: (state, action) => {
      state.user = action.payload;
      persist(state);
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.mustChangePassword = false;
      state.error = null;
      persist(state);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.mustChangePassword = !!action.payload.mustChangePassword;
        persist(state);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur de connexion.';
      })
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resetPasswordWithOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPasswordWithOtp.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPasswordWithOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.mustChangePassword = false;
        persist(state);
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.user = action.payload;
        persist(state);
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        persist(state);
      })
      .addCase(uploadProfilePicture.fulfilled, (state, action) => {
        state.user = action.payload;
        persist(state);
      });
  },
});

export const { setCredentials, setUser, logout, clearError } = authSlice.actions;
export default authSlice.reducer;
